import * as React from 'react'
import styled from 'styled-components'
import * as constants from 'static/constants'
import Guest from 'components/organisms/Tours/Weekly/Drawer/Guest'
import SvgIcon from 'components/atoms/SvgIcon'
import { IBuildedTour } from 'utils/weeklyTypes'
import ClassNames from 'classnames'
import { useWeeklyContext } from 'core/contexts/weekly/WeeklyContext'
import {
  updatedTourByOtaReservation,
  getTourGroupStyleClass,
  sortedOtaReservations,
} from 'utils/weekly'
import { useTourDrawerContext } from 'core/contexts/tour/TourDrawerContext'

interface IProps {
  tour: IBuildedTour
  handleClearSelect: () => void
}

const GuestPanel: React.VFC<IProps> = ({ tour, handleClearSelect }) => {
  const { onTourUpdate } = useWeeklyContext()
  const {
    selectedGroupName,
    justAssignedOtaReservations,
    setJustAssignedOtaReservations,
    selectedOtaReservations,
    setSelectedOtaReservations,
    groupSelectedId,
    orderDesc,
    setOrderDesc,
    groupSelectingResource,
    setGroupSelectingResource,
    selectedTourGroup,
  } = useTourDrawerContext()

  const tourGroups = tour.tour_groups
  const [guestNameInput, setGuestNameInput] = React.useState('')
  const [guestNameKeywords, setGuestNameKeywords] = React.useState([])

  const tourGroupClassName = React.useMemo(() => {
    return selectedTourGroup ? getTourGroupStyleClass(selectedTourGroup) : ''
  }, [selectedTourGroup])

  const handleChangeOrder = React.useCallback(() => {
    const newOrderDesc = !orderDesc
    setOrderDesc(newOrderDesc)
    setSelectedOtaReservations(sortedOtaReservations(selectedOtaReservations, newOrderDesc))
  }, [selectedOtaReservations, orderDesc, setOrderDesc, setSelectedOtaReservations])

  React.useEffect(() => {
    if (guestNameInput.length > 1) {
      const keywords = guestNameInput.split(/[\s　]+/).filter(keyword => keyword.trim() !== '')
      setGuestNameKeywords(keywords)
    } else {
      setGuestNameKeywords([])
    }
  }, [guestNameInput, setGuestNameKeywords])

  const filteredOtaReservations = React.useMemo(() => {
    const uniqIds = new Set<number>()
    const justAssignedIds = new Set(justAssignedOtaReservations.map(jr => jr.id))
    return selectedOtaReservations
      .concat(justAssignedOtaReservations) // "グループ未アサインのゲスト" を選択すると selectedOtaReservations に含まれないため、配列自体を足す
      .filter(ota_reservation => {
        // selectedOtaReservations + justAssignedOtaReservations での重複を排除
        if (uniqIds.has(ota_reservation.id)) {
          return false
        }
        uniqIds.add(ota_reservation.id)

        const justAssigned = justAssignedIds.has(ota_reservation.id)
        const lowerName = ota_reservation.customer_full_name.toLowerCase()
        const doFilter = guestNameKeywords.length > 0
        const filtered = guestNameKeywords.every(keyword =>
          lowerName.includes(keyword.toLowerCase())
        )

        return justAssigned || !doFilter || filtered
      })
      .filter(Boolean)
  }, [selectedOtaReservations, justAssignedOtaReservations, guestNameKeywords])

  const handleClearFilter = () => {
    setGuestNameInput('')
    setGuestNameKeywords([])
  }

  const handleAfterUpdateOtaReservation = React.useCallback(
    (ota_reservation, params) => {
      onTourUpdate(updatedTourByOtaReservation(tour, ota_reservation.id, params))
    },
    [tour]
  )

  return (
    <RightPanelWrapper
      className={`${selectedGroupName ? ' groupSelected' : ''} ${tourGroupClassName}`}
    >
      <div className="title">
        <div className="leftInner">
          <div className="category">
            予約一覧
            <span
              className="sortButton"
              onClick={handleChangeOrder}
              role="button"
              aria-label="Sort Reaservation list"
            >
              {orderDesc ? (
                <SvgIcon name="sort_desc" className="icon" ariaHidden={true} role="presentation" />
              ) : (
                <SvgIcon name="sort_asc" className="icon" ariaHidden={true} role="presentation" />
              )}
            </span>
          </div>
          <GuestSearch className={ClassNames({ hasValue: guestNameKeywords.length > 0 })}>
            <SvgIcon name="filter_list" className="icon" ariaHidden={true} role="presentation" />
            <input
              name="search-guest"
              type="search"
              className="minimumInput"
              value={guestNameInput}
              onChange={e => {
                setGuestNameInput(e.target.value)
              }}
              placeholder="ゲスト名検索"
              aria-label="ゲスト名検索"
            />
            <div onClick={handleClearFilter} className="clearFilter" role="button">
              <SvgIcon name="cancel" className="icon" ariaHidden={true} role="presentation" />
            </div>
          </GuestSearch>
        </div>
      </div>

      <div className="rightPanelInner">
        {!!selectedGroupName && (
          <div className="selectedGroupName">
            {selectedGroupName}の予約を表示中{' '}
            <span onClick={handleClearSelect} role="button">
              閉じる
            </span>
          </div>
        )}
        {selectedOtaReservations.length === 0 && (
          <div className="emptyMessage">ゲストが存在しません</div>
        )}
        {filteredOtaReservations.map(otaReservation => (
          <Guest
            otaReservation={otaReservation}
            handleAfterUpdate={handleAfterUpdateOtaReservation}
            setGroupSelectingResource={setGroupSelectingResource}
            groupSelectingResource={groupSelectingResource}
            groupSelectedId={groupSelectedId}
            guide={tourGroups.find(tg => tg.id === otaReservation.jwt_tour_group_id)?.assignedGuide}
            setJustAssignedOtaReservations={setJustAssignedOtaReservations}
            justAssignedOtaReservations={justAssignedOtaReservations}
            key={otaReservation.id}
          />
        ))}
      </div>
    </RightPanelWrapper>
  )
}

const GuestSearch = styled.div`
  display: flex;
  align-items: center;
  font-size: 14px;
  width: 140px;
  height: 32px;
  gap: 4px;
  color: ${constants.COLORS.subText};
  border-bottom: 1px solid transparent;
  &:has(.minimumInput:focus) {
    border-bottom-color: ${constants.COLORS.divider};
  }
  .icon {
    width: 20px;
    height: 20px;
    flex: none;
  }
  .clearFilter {
    display: none;
    flex: 0 0 auto;
    .icon {
      width: 20px;
      height: 20px;
      color: ${constants.COLORS.subText};
      transition: color 0.2s;
      &:hover {
        color: ${constants.COLORS.black};
      }
    }
  }

  .minimumInput {
    width: 100%;
    height: 24px;
    padding: 0 0 2px;
    outline: none;
    border: none;
    background-color: transparent;
    color: ${constants.COLORS.subText};
    &::-webkit-search-cancel-button,
    &::-webkit-search-decoration,
    &::-webkit-search-results-button,
    &::-webkit-search-results-decoration {
      display: none;
    }
    &::-ms-clear,
    &::-ms-reveal {
      display: none;
      width: 0;
      height: 0;
    }
  }
  &.hasValue {
    border-bottom-color: ${constants.COLORS.divider};
    .clearFilter {
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }
  }
`

const RightPanelWrapper = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${constants.COLORS.background2};
  height: 100%;
  &.groupSelected {
    .title,
    .rightPanelInner {
      background-color: ${constants.COLORS.successBG2} !important;
    }
  }

  .rightPanelInner {
    flex: 1 1 0;
    padding: 0 16px 40px;
    overflow-x: hidden;
    overflow-y: auto;
    transition: background-color 0.2s;
  }

  .emptyMessage {
    height: calc(100% - 44px);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px 16px 40px;
    color: ${constants.COLORS.subText};
    font-size: 14px;
    text-align: center;
  }

  .title {
    flex: 0 0 auto;
    position: sticky;
    top: 0;
    display: flex;
    justify-content: space-between;
    z-index: 1;

    .count {
      color: ${constants.COLORS.primary};
    }

    .leftInner {
      display: inline-flex;
      align-items: center;
      gap: 12px;
    }

    .category {
      display: flex;
      align-items: center;
      color: ${constants.COLORS.subText};
      .sortButton {
        margin-left: 4px;
        transition: opacity 0.2s;
        cursor: pointer;
        &:hover {
          opacity: 0.75;
        }
      }
    }

    .Button {
      display: flex;
      align-items: center;
      line-height: 24px;
      padding: 4px 16px 4px 12px;
      .icon {
        width: 20px;
        height: 20px;
        margin-right: 4px;
      }
    }
  }

  .selectedGroupName {
    line-height: 20px;
    padding: 4px;
    margin-bottom: 16px;
    font-size: 12px;
    text-align: center;
    background-color: ${constants.COLORS.white};
    border-radius: 4px;
    span {
      margin-left: 12px;
      color: ${constants.COLORS.primary};
      text-decoration: underline;
      cursor: pointer;
      &:hover {
        text-decoration: none;
      }
    }
  }
`

export default GuestPanel
